import { useEffect, useMemo, useRef } from 'react';
import {
  PayLinkError,
  PayLinkRequestError,
  PayLinkUnreachableError,
  TransactionView,
} from '@baemingo/loomis-pay';
import { useStorage } from '~Components/Storage';
import { useBridgeApi, useI18n, useLogger } from '~Hooks';
import { createLoomisClient } from '~Utils/loomis';
import { MethodProps } from '../types';

const INTERVAL_MS = 1000;

/**
 * After a cancel, how long Teller may stay unreachable before the payment is
 * given up. Teller ends a cancelled sale within about 30 seconds of the cancel.
 */
const GIVE_UP_AFTER_CANCEL_MS = 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A Loomis Pay terminal on the venue's network, at the IP set in Payment
 * settings. Teller, Loomis' payment app on it, serves PayLink
 * (`@baemingo/loomis-pay`) and takes the card on its own screen, with its own
 * cancel.
 *
 * Teller is the only source of truth: the backend never hears of a Loomis
 * payment. It dedupes on the teller_transaction_id, here the payment's id:
 * asking for the same sale again never charges twice, so a lost request or
 * reply is simply asked again until Teller says how the sale ended.
 *
 * Teller asks for the tip itself when it is set up to, so OM's tipping
 * setting has no effect; whatever the customer added comes back as gratuity.
 */
const LoomisPayment: React.FunctionComponent<MethodProps> = ({
  payment,
  order,
  amount,
  onComplete,
  onFail,
}) => {
  const logger = useLogger('payment:loomis');
  const { i18n } = useI18n();
  const bridgeApi = useBridgeApi();
  const [{ loomisTerminalIp }] = useStorage();
  const client = useMemo(
    () => (loomisTerminalIp ? createLoomisClient(bridgeApi.fetch, loomisTerminalIp) : null),
    [],
  );
  const cancelledAt = useRef<number | null>(null);

  /** Staff pressed cancel on OM. Teller decides how the sale ends. */
  useEffect(() => {
    if (!client || !payment.meta.cancelRequested || cancelledAt.current) {
      return;
    }

    cancelledAt.current = Date.now();
    logger.info('Loomis: cancelling payment', { id: payment.id });

    // Accepted at once, but Teller only acts on it when its card wait ends
    client.cancel().catch((e) => {
      logger.warn(`Loomis: cancel failed: ${(e as Error).message}`, { id: payment.id });
    });
  }, [payment.meta.cancelRequested]);

  useEffect(() => {
    if (!client) {
      logger.warn('Loomis: no terminal IP set', { id: payment.id });
      onFail(i18n('Settings.Payments.Loomis.NoTerminal'));
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const body = { teller_transaction_id: payment.id, amount: String(amount) };

    logger.info(`Loomis: starting payment of ${amount! / 100}`, {
      id: payment.id,
      orderId: order?.id,
    });

    const pay = async (): Promise<void> => {
      for (;;) {
        let view: TransactionView<'sales'>;

        try {
          // eslint-disable-next-line no-await-in-loop
          view = await client.run('sales', body, {
            waitMs: 20_000,
            intervalMs: INTERVAL_MS,
            signal,
          });
        } catch (error) {
          if (signal.aborted) {
            return;
          }

          if (error instanceof PayLinkError && error.status === 400) {
            // Refused before it started: nothing was asked of a card
            onFail(error.failure?.message ?? 'The terminal refused the payment.');
            return;
          }

          if (error instanceof PayLinkRequestError) {
            // Never sent
            logger.error(`Loomis: ${error.message}`, { id: payment.id });
            onFail('The terminal refused the payment.');
            return;
          }

          if (cancelledAt.current && Date.now() - cancelledAt.current > GIVE_UP_AFTER_CANCEL_MS) {
            logger.error('Loomis: gave up on a cancelled payment Teller never answered for', {
              id: payment.id,
              reason: (error as Error).message,
            });
            onFail('Cancelled. The terminal did not say how the payment ended.');
            return;
          }

          if (!(error instanceof PayLinkUnreachableError)) {
            logger.warn(`Loomis: ${(error as Error).message}`, { id: payment.id });
          }

          // No answer: the money may have moved, so ask Teller again
          // eslint-disable-next-line no-await-in-loop
          await sleep(INTERVAL_MS);
          continue;
        }

        if (signal.aborted) {
          return;
        }

        switch (view.status) {
          case 'approved':
            logger.info('Loomis: payment approved', { id: payment.id });
            onComplete(null, {
              // Loomis' own id for the payment, as the pspReference is for an Adyen one
              transactionId: view.transactionId ?? view.tellerTransactionId,
              tip: view.gratuity ?? 0,
              cardDetails: {
                cardType: view.scheme,
                maskedPan: view.maskedPan,
              },
            });
            return;
          case 'declined':
          case 'cancelled':
            logger.info('Loomis: payment declined', {
              id: payment.id,
              failureReason: view.failureReason,
            });
            onFail(view.message ?? view.failureReason ?? 'Declined');
            return;
          case 'busy':
            // Teller is running another transaction and did not start this one
            onFail('The terminal is busy with another payment.');
            return;
          case 'unknown':
            logger.warn('Loomis: Teller answered something unexpected', {
              id: payment.id,
              violations: view.violations,
            });
            // eslint-disable-next-line no-await-in-loop
            await sleep(INTERVAL_MS);
            break;
          default:
            // Still waiting for the card
            break;
        }
      }
    };

    pay();

    return () => {
      controller.abort();
    };
  }, []);

  return null;
};

export default LoomisPayment;
